import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import { PeopleAlt, PersonAdd, Delete as DeleteIcon } from '@mui/icons-material';
import { DynamicContainer } from '../components/common/DynamicContainer';
import { useUserRights } from '../context/UserRightsContext';
import { toast } from 'react-toastify';
import { fetchTenantSubscription, getUserSeatLimit } from '../services/licensingApi';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/user`;
const token = localStorage.getItem('token');
const tenantId = localStorage.getItem('tenantId');

const TenantUsers = () => {
  const { ROLES_API_BASE, getRolesHeaders } = useUserRights();
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [tenantRoles, setTenantRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [seatLimit, setSeatLimit] = useState({
    maxUsers: null,
    usedUsers: null,
    remaining: null,
    atLimit: false,
  });

  // Check if user is CDX user
  const isCdxUser = () => {
    try {
      const cdxUser = localStorage.getItem('cdx_user');
      return cdxUser === 'true';
    } catch (error) {
      console.error('Error reading cdx_user from localStorage:', error);
      return false;
    }
  };

  const loadSeatLimit = useCallback(async (fallbackUsed = null) => {
    const tid = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
    if (!tid) {
      setSeatLimit({ maxUsers: null, usedUsers: null, remaining: null, atLimit: false });
      return;
    }
    try {
      const sub = await fetchTenantSubscription(tid);
      setSeatLimit(getUserSeatLimit(sub, fallbackUsed));
    } catch (e) {
      console.warn('Could not load user seat limit:', e);
      setSeatLimit({ maxUsers: null, usedUsers: null, remaining: null, atLimit: false });
    }
  }, []);
 
  // Add useEffect to fetch data on component mount
  useEffect(() => {
    (async () => {
      const catalog = await fetchTenantRoles();
      const list = await fetchUsers(catalog);
      await loadSeatLimit(Array.isArray(list) ? list.length : null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
 
  const fetchUsers = async (rolesCatalog = null) => {
    setIsLoading(true);
    try {
      console.log('Fetching users...'); // Debug log
      const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
 
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }
 
      const data = await response.json();
      console.log('Users response:', data); // Debug log

      const currentTenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
      const headers = getRolesHeaders();
      let rolesByUserId = {};
      if (currentTenantId) {
        try {
          const urResp = await fetch(`${ROLES_API_BASE}/user/tenant/${currentTenantId}`, { headers });
          if (urResp.ok) {
            const userRoles = await urResp.json();
            (Array.isArray(userRoles) ? userRoles : []).forEach((ur) => {
              const uid = ur.userId != null ? String(ur.userId) : null;
              if (!uid) return;
              // Prefer first role; join names if multiple
              if (!rolesByUserId[uid]) {
                rolesByUserId[uid] = [];
              }
              rolesByUserId[uid].push(ur.roleId);
            });
          }
        } catch (roleErr) {
          console.warn('Could not load user roles for grid:', roleErr);
        }
      }

      const catalog = rolesCatalog || allRoles;
 
      // Map the response data to match your column definitions and add an id field
      const mappedData = data.map(item => {
        const cdx = item.cdxUser === true || item.cdx_user === true;
        const guId = item.globalUserId != null ? String(item.globalUserId) : null;
        const roleIds = guId && rolesByUserId[guId] ? rolesByUserId[guId] : [];
        const roleNames = roleIds
          .map((rid) => {
            const info = (catalog || []).find((r) => String(r.id) === String(rid));
            return info?.roleName || null;
          })
          .filter(Boolean);
        return {
          ...item,
          id: item.userId,
          cdxUser: cdx,
          cdxUserDisplay: cdx ? 'Yes' : 'No',
          roleId: roleIds[0] || '',
          roleName: roleNames.length > 0 ? roleNames.join(', ') : '—',
        };
      });

      console.log('Mapped data for grid:', mappedData);
      setUsers(mappedData);

      return mappedData;
    } catch (error) {
      console.error('Error details:', error); // Debug log
      toast.error(`Error fetching users: ${error.message}`);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      if (seatLimit.atLimit) {
        throw new Error(
          `License user limit reached (${seatLimit.usedUsers}/${seatLimit.maxUsers}). Remove a user or upgrade the plan.`
        );
      }

      // Remove empty fields or null values that might cause validation issues
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, v]) => v !== null && v !== '')
      );

      // ALWAYS set username to email (this is the key change)
      if (cleanData.userEmail) {
        cleanData.username = cleanData.userEmail.trim(); // Ensure username matches email
      } else {
        throw new Error('Email is required');
      }

      // Add tenant ID from localStorage
      if (tenantId) {
        cleanData.tenantId = parseInt(tenantId);
      }

      // Role is mandatory
      if (!cleanData.roleId) {
        throw new Error('Role is required');
      }

      console.log('Creating user with clean data:', cleanData);
      console.log('Username set to:', cleanData.username); // Debug log

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(cleanData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Create response:', responseData);

      // Assign role to user after successful user creation
      if (responseData.globalUserId && cleanData.roleId) {
        try {
          const headers = getRolesHeaders();
          const roleResp = await fetch(`${ROLES_API_BASE}/user`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: responseData.globalUserId,
              tenantId: parseInt(tenantId),
              roleId: cleanData.roleId,
            }),
          });

          if (!roleResp.ok) {
            console.warn('User created but role assignment failed');
          }
        } catch (roleError) {
          console.warn('User created but role assignment failed:', roleError);
        }
      }

      toast.success('User created successfully');
      const list = await fetchUsers(allRoles);
      await loadSeatLimit(Array.isArray(list) ? list.length : null);
      return responseData;
    } catch (error) {
      console.error('Create error full details:', error);
      toast.error(`Error creating user: ${error.message}`);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const {
        password: _password,
        cdxUserDisplay: _cdxDisp,
        cdxUser: _cdx,
        ...updateData
      } = data;

      // ALWAYS set username to email when email is present (this is the key change)
      if (updateData.userEmail) {
        updateData.username = updateData.userEmail.trim(); // Ensure username always matches email
        console.log('Email updated, setting username to:', updateData.username); // Debug log
      }
      // Add tenant ID if not already present
      if (tenantId && !updateData.tenantId) {
        updateData.tenantId = parseInt(tenantId);
      }
      console.log('Updating user:', id, updateData);
      console.log('Username in update payload:', updateData.username); // Debug log

      // Role is mandatory
      if (!updateData.roleId) {
        throw new Error('Role is required');
      }

      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Update response:', responseData);

      // Update user role after successful user update
      const user = users.find(u => u.id === id);

      if (user && user.globalUserId && updateData.roleId) {
        try {
          const headers = getRolesHeaders();

          // First, remove all existing roles for this user/tenant
          const existingRolesResp = await fetch(`${ROLES_API_BASE}/user/${user.globalUserId}/tenant/${tenantId}`, { headers });
          if (existingRolesResp.ok) {
            const existingRoles = await existingRolesResp.json();
            if (Array.isArray(existingRoles)) {
              for (const role of existingRoles) {
                await fetch(`${ROLES_API_BASE}/user/${user.globalUserId}/tenant/${tenantId}/role/${role.roleId}`, {
                  method: 'DELETE',
                  headers
                });
              }
            }
          }

          // Then assign the new role
          const roleResp = await fetch(`${ROLES_API_BASE}/user`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: user.globalUserId,
              tenantId: parseInt(tenantId),
              roleId: updateData.roleId,
            }),
          });

          if (!roleResp.ok) {
            console.warn('User updated but role assignment failed');
          }
        } catch (roleError) {
          console.warn('User updated but role assignment failed:', roleError);
        }
      }

      if (responseData.success) {
        toast.success(responseData.message || 'User updated successfully');
      } else {
        throw new Error(responseData.error || 'Update operation failed');
      }
      await fetchUsers(allRoles);
      return responseData;
    } catch (error) {
      console.error('Update error full details:', error);
      toast.error(`Error updating user: ${error.message}`);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    try {
      // Using userId for delete operations
      console.log('Deleting user:', id);
 
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
 
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }
 
      const responseData = await response.json();
      console.log('Delete response:', responseData);
      
      if (responseData.success) {
        toast.success(responseData.message || 'User deleted successfully');
      } else {
        throw new Error(responseData.error || 'Delete operation failed');
      }

      const list = await fetchUsers(allRoles);
      await loadSeatLimit(Array.isArray(list) ? list.length : null);
      return true;
    } catch (error) {
      console.error('Delete error full details:', error);
      toast.error(`Error deleting user: ${error.message}`);
      throw error;
    }
  };

  const fetchTenantRoles = async () => {
    try {
      const headers = getRolesHeaders();
      const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
      if (!tenantId) return [];

      const [trResp, rolesResp] = await Promise.all([
        fetch(`${ROLES_API_BASE}/tenant/tenant/${tenantId}`, { headers }),
        fetch(`${ROLES_API_BASE}`, { headers }),
      ]);

      if (!trResp.ok || !rolesResp.ok) return [];

      const trData = await trResp.json();
      const rolesData = await rolesResp.json();
      const tenantRolesList = Array.isArray(trData) ? trData : [];
      const allRolesList = Array.isArray(rolesData) ? rolesData : [];

      setTenantRoles(tenantRolesList);
      setAllRoles(allRolesList);
      return allRolesList;
    } catch (error) {
      console.error('Error fetching tenant roles:', error);
      return [];
    }
  };

  const getUserRole = async (userId) => {
    try {
      const headers = getRolesHeaders();
      const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
      if (!userId || !tenantId) return null;

      const resp = await fetch(`${ROLES_API_BASE}/user/${userId}/tenant/${tenantId}`, { headers });
      if (!resp.ok) return null;

      const userRoles = await resp.json();
      return Array.isArray(userRoles) && userRoles.length > 0 ? userRoles[0].roleId : null;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  };

  const prepareEditData = async (rowData) => {
    const userRole = await getUserRole(rowData.globalUserId || rowData.userId);
    const cdx = rowData.cdxUser === true || rowData.cdx_user === true;
    return {
      ...initialFormData,
      ...rowData,
      roleId: userRole || '',
      cdxUserDisplay: cdx ? 'Yes' : 'No',
    };
  };

  // Define columns based on the API response structure
  const columns = [
    { field: 'userId', headerName: 'User ID', width: 80 },
    { field: 'userEmail', headerName: 'Email', width: 200 },
    { field: 'userFirstName', headerName: 'First Name', width: 130 },
    { field: 'userLastName', headerName: 'Last Name', width: 130 },
    { field: 'roleName', headerName: 'Role', width: 180 },
    { field: 'cdxUserDisplay', headerName: 'CDX user', width: 100 },
    { field: 'userPhone', headerName: 'Phone', width: 150 },
    { field: 'status', headerName: 'Status', width: 100 },
    { field: 'startDate', headerName: 'Start Date', width: 120 },
    { field: 'endDate', headerName: 'End Date', width: 120 },
    { field: 'address', headerName: 'Address', width: 200 },
  ];
 
  // Form fields for creating/editing users
  const getRoleOptions = () => {
    return tenantRoles.map((tr) => {
      const roleInfo = allRoles.find((r) => r.id === tr.roleId);
      return {
        value: tr.roleId,
        label: roleInfo ? roleInfo.roleName : 'Unknown Role',
      };
    });
  };

  const formFields = [
    {
      name: 'userEmail',
      label: 'Email',
      type: 'email',
      required: true,
      validate: (value) => {
        if (!value) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        return null;
      }
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: true,
      validate: (value) => {
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return null;
      },
      hideOnEdit: true
    },
    {
      name: 'userFirstName',
      label: 'First Name',
      required: true,
      validate: (value) => {
        if (!value) return 'First Name is required';
        return null;
      }
    },
    {
      name: 'userLastName',
      label: 'Last Name',
      required: true,
      validate: (value) => {
        if (!value) return 'Last Name is required';
        return null;
      }
    },
    {
      name: 'roleId',
      label: 'Role',
      type: 'select',
      required: true,
      options: getRoleOptions(),
      validate: (value) => {
        if (!value) return 'Role is required';
        return null;
      }
    },
    {
      name: 'cdxUserDisplay',
      label: 'CDX user',
      disabled: true,
      hideOnCreate: true,
    },
    {
      name: 'userPhone',
      label: 'Phone Number',
      validate: (value) => {
        if (value && !/^\+?\d{1,15}$/.test(value)) return 'Please enter a valid phone number';
        return null;
      }
    },
    {
      name: 'status',
      label: 'Status',
      type: 'number',
      validate: (value) => {
        if (value && !/^\d+$/.test(value)) return 'Status must be a number';
        return null;
      }
    },
    {
      name: 'address',
      label: 'Address',
      multiline: true
    }
  ];
 
  const initialFormData = {
    userEmail: '',
    password: '',
    userFirstName: '',
    userLastName: '',
    userPhone: '',
    status: 1,
    address: '',
  };

  const seatChip =
    seatLimit.maxUsers != null ? (
      <Tooltip
        title={
          seatLimit.atLimit
            ? 'License user limit reached. Remove a user or upgrade the plan to add more.'
            : `Licensed seats: ${seatLimit.usedUsers ?? '—'}/${seatLimit.maxUsers}`
        }
      >
        <Chip
          size="small"
          variant="outlined"
          color={seatLimit.atLimit ? 'primary' : 'default'}
          label={`Users ${seatLimit.usedUsers ?? '—'}/${seatLimit.maxUsers}`}
          sx={{ mr: 0.5 }}
        />
      </Tooltip>
    ) : null;

  return (
    <>
      <DynamicContainer
        title="Tenant Users"
        icon={PeopleAlt}
        columns={columns}
        formFields={formFields}
        initialFormData={initialFormData}
        idField="id"
        rows={users}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onRowDoubleClick={null}
        isLoading={isLoading}
        prepareEditData={prepareEditData}
        isCdxUser={isCdxUser()}
        functionCode="Users"
        hideAdd={seatLimit.atLimit}
        headerExtra={seatChip}
      />
    </>
  );
};

export default TenantUsers;